import Edit16 from '@carbon/icons-react/es/edit/16';
import { Switch } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import { Automation } from 'api/automations';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Table } from 'components/molecules/table';
import {
  AutomationByReferenceId,
  useDeleteAutomation,
  useEditAutomation,
  useGetAutomationsForReferenceId,
} from 'hooks/use-automations';
import { refToAutomationSource } from 'utils/automations';
import { KeyEventListItem } from './key-event-list-item';

const StyledTable = styled(Table)`
  margin: 0 var(--spacing-05);
  .ant-table-tbody > tr > td {
    padding-top: 0;
  }
  .ant-table-tbody > tr > td:first-child {
    padding-top: var(--spacing-05);
  }
`;

interface Props {
  automations: Automation[];
  refType: 'phase' | 'task';
  refId: number;
  templateId: number;
}

interface DataType {
  automation: Automation;
  enable: { automation: Automation } & Omit<Props, 'automations'>;
  more: { automation: Automation } & Omit<Props, 'automations'>;
}

const MoreActionsMenu = ({
  automation,
  refId,
  refType,
  templateId,
}: {
  automation: Automation;
} & Omit<Props, 'automations'>): JSX.Element | null => {
  const { mutateAsync } = useDeleteAutomation();
  const qc = useQueryClient();
  const { data: automations } = useGetAutomationsForReferenceId({
    templateId,
    refId,
    refType: refToAutomationSource(refType),
  });

  const capExp = automations?.find(
    (e) =>
      e.tags.fieldId === automation.actionsV2.at(0)?.input.fieldId &&
      e.tags.keyEventAutomationType === 'capture-expected' &&
      e.tags.referenceId === refId &&
      e.tags.referenceType === refToAutomationSource(refType),
  );

  const upExp = automations?.find(
    (e) =>
      e.tags.fieldId === automation.actionsV2.at(0)?.input.fieldId &&
      e.tags.keyEventAutomationType === 'update-expected' &&
      e.tags.referenceId === refId &&
      e.tags.referenceType === refToAutomationSource(refType),
  );

  const history = useHistory();
  const location = useLocation();

  const menu = (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        key="edit"
        icon={<Edit16 />}
        onClick={() => {
          history.push(
            `${location.pathname}/automations/${automation.automationId}/${refType}/${refId}`,
          );
        }}
      >
        <FormattedMessage id="edit" />
      </Menu.Item>
      <Menu.ItemDelete
        key="delete"
        onClick={() => {
          mutateAsync({ automationId: automation.automationId }).then(
            async () => {
              if (capExp) {
                await mutateAsync({ automationId: capExp.automationId });
              }
              if (upExp) {
                await mutateAsync({ automationId: upExp.automationId });
              }
              await qc.refetchQueries({
                queryKey: AutomationByReferenceId(
                  refToAutomationSource(refType),
                  Number(refId),
                ),
              });
            },
          );
        }}
      />
    </Menu>
  );

  return <MoreActionsDropdown menu={menu} />;
};

const EnabledDisabledWrapper = styled.span`
  margin-left: var(--spacing-01);
`;

const AutomationEnableSwitch = ({
  automation: { enabled, automationId },
  refType,
  refId,
}: {
  automation: Automation;
} & Omit<Props, 'automations'>) => {
  const { mutateAsync } = useEditAutomation();
  const qc = useQueryClient();

  return (
    <>
      <Switch
        size="small"
        checked={enabled}
        onChange={async (newState) => {
          mutateAsync({
            automation: { enabled: newState },
            automationId,
          }).then(() => {
            qc.refetchQueries(
              AutomationByReferenceId(
                refToAutomationSource(refType),
                Number(refId),
              ),
            );
          });
        }}
      />
      <EnabledDisabledWrapper>
        {enabled ? (
          <FormattedMessage id="enabled" />
        ) : (
          <FormattedMessage id="disabled" />
        )}
      </EnabledDisabledWrapper>
    </>
  );
};

const columns = (templateId: number): ColumnsType<DataType> => [
  {
    title: 'Key Event',
    dataIndex: 'automation',
    key: 'automation',
    render: (automation) => <KeyEventListItem automation={automation} />,
  },
  {
    title: 'Status',
    dataIndex: 'enable',
    key: 'enable',
    width: 150,
    render: ({ automation, refType, refId }) => (
      <AutomationEnableSwitch
        templateId={templateId}
        automation={automation}
        refId={refId}
        refType={refType}
      />
    ),
  },
  {
    title: '',
    dataIndex: 'more',
    key: 'more',
    render: ({ automation, refType, refId }) => (
      <MoreActionsMenu
        templateId={templateId}
        automation={automation}
        refType={refType}
        refId={refId}
      />
    ),
  },
];

export const ListKeyEvents: React.FC<Props> = ({
  automations,
  refId,
  refType,
  templateId,
}) => {
  return (
    <StyledTable
      pagination={false}
      columns={columns(templateId)}
      dataSource={automations.map((e) => ({
        automation: e,
        enable: { automation: e, refType, refId },
        more: { automation: e, refType, refId },
      }))}
    />
  );
};
